import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, Package, FileText, Award } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Walnuts = () => {
  const products = [
    {
      id: 'lhp',
      name: 'Light Halves & Pieces (LHP)',
      bestFor: 'Premium retail, snacking, bakery toppings',
      gradeMeaning: 'Light-colored halves; premium appearance grade',
      qualityFocus: 'Uniform light color, minimal defects',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/9613e5729ed3d67c1a9ef55c72645997a50dceb5bcaa92958b33a167e32d4c1f.png'
    },
    {
      id: 'halves',
      name: 'Walnut Halves',
      bestFor: 'Premium retail packs, gifting, HoReCa',
      gradeMeaning: 'Complete half kernels; highest grade',
      qualityFocus: 'Intact halves, uniform size, premium look',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/9613e5729ed3d67c1a9ef55c72645997a50dceb5bcaa92958b33a167e32d4c1f.png'
    },
    {
      id: 'pieces',
      name: 'Walnut Pieces',
      bestFor: 'Bakery, confectionery, ingredient manufacturing',
      gradeMeaning: 'Broken pieces; cost-effective for processing',
      qualityFocus: 'Clean pieces, controlled size distribution',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/9613e5729ed3d67c1a9ef55c72645997a50dceb5bcaa92958b33a167e32d4c1f.png'
    },
    {
      id: 'quarters',
      name: 'Walnut Quarters',
      bestFor: 'Salads, toppings, snack mixes',
      gradeMeaning: 'Quarter-size pieces; good visual appeal',
      qualityFocus: 'Uniform quarter size, minimal fines',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/9613e5729ed3d67c1a9ef55c72645997a50dceb5bcaa92958b33a167e32d4c1f.png'
    }
  ];

  const colorGrades = [
    { name: 'Extra Light', description: 'Lightest color; premium appearance for retail' },
    { name: 'Light', description: 'Light amber; most popular grade' },
    { name: 'Light Amber', description: 'Slightly darker; good value option' },
    { name: 'Amber', description: 'Medium color; suitable for processing' }
  ];

  const grades = [
    {
      name: 'Standard Export',
      description: 'Best for bulk sourcing and value-led programs',
      features: ['Standard grading + batch COA', 'Aligned to buyer/destination specifications', 'Cost-effective bulk supply']
    },
    {
      name: 'Premium (EU/USA-Ready)',
      description: 'Best for private label and strict QC retail programs',
      features: ['Tighter QC + enhanced traceability', 'Premium packaging options', 'Expanded COA panel as required']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products/dry-fruits" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dry Fruits
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Walnuts</h1>
              <p className="text-xl text-slate-700 mb-6">
                Premium walnut halves and pieces from California and Chile for bakery, snacks, and manufacturing
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-[#263776] text-white px-4 py-2 rounded-full">Multiple Color Grades</span>
                <span className="bg-[#F47D20] text-white px-4 py-2 rounded-full">California · Chile Origin</span>
                <span className="bg-slate-700 text-white px-4 py-2 rounded-full">Batch COA Always Supplied</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grading Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Grading Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {grades.map((grade, index) => (
                <Card key={index} className="p-6 border-t-4 border-t-[#263776]">
                  <h3 className="text-xl font-bold text-[#263776] mb-2">{grade.name}</h3>
                  <p className="text-slate-600 mb-4">{grade.description}</p>
                  <ul className="space-y-2">
                    {grade.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product Forms */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Walnut Products</h2>
              <p className="text-lg text-slate-600">Halves, pieces, and quarters for different applications</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#263776] mb-2">{product.name}</h3>
                    <p className="text-xs text-[#F47D20] font-medium mb-3">{product.bestFor}</p>
                    
                    <Accordion type="single" collapsible className="mb-3">
                      <AccordionItem value="details" className="border-none">
                        <AccordionTrigger className="text-xs py-1">View Details</AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-1 text-xs">
                            <p><strong>Grade:</strong> {product.gradeMeaning}</p>
                            <p><strong>Quality:</strong> {product.qualityFocus}</p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <Button asChild size="sm" className="w-full bg-[#263776] hover:bg-[#1e2a5e] text-xs">
                      <Link to={`/contact?product=walnuts-${product.id}`}>Request Quote</Link>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Color Grades */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Color Grades Available</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {colorGrades.map((grade, index) => (
                <Card key={index} className="p-4 text-center border-l-4 border-l-[#F47D20]">
                  <h3 className="font-bold text-[#263776] mb-2">{grade.name}</h3>
                  <p className="text-sm text-slate-600">{grade.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Baseline */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-[#263776] mb-6 text-center">SPANXPORT Quality Baseline</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#263776] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Moisture Control</h3>
                  <p className="text-sm text-slate-600">Typically 3-5% for extended shelf life</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#F47D20] rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Traceability</h3>
                  <p className="text-sm text-slate-600">Lot/production/date codes for full traceability</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Testing</h3>
                  <p className="text-sm text-slate-600">Rancidity, aflatoxin, and microbiological testing</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Request a Quote for Walnuts</h2>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Share your requirements and we'll respond with offer + lead time + documentation list
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Detailed Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products/dry-fruits">Back to Dry Fruits</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Walnuts;
